import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Alert,
  Card,
  CardContent,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
  AccountBalance as BankIcon,
  QrCode2 as QRIcon,
  LocalOffer as DiscountIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Calculate as CalculateIcon,
} from "@mui/icons-material";

const NewBilling = () => {
  // Sample products data
  const productsData = [
    { id: 1, code: "PROD001", name: "Samsung Galaxy S23", category: "Electronics", price: 79999, tax: 18, stock: 45, unit: "Pcs" },
    { id: 2, code: "PROD002", name: "Dell Laptop i5 11th Gen", category: "Electronics", price: 54999, tax: 18, stock: 28, unit: "Pcs" },
    { id: 3, code: "PROD003", name: "Nike Running Shoes", category: "Apparel", price: 4999, tax: 12, stock: 120, unit: "Pair" },
    { id: 4, code: "PROD004", name: "Adidas Track Pants", category: "Apparel", price: 2499, tax: 12, stock: 85, unit: "Pcs" },
    { id: 5, code: "PROD005", name: "Prestige Pressure Cooker 5L", category: "Home & Kitchen", price: 3499, tax: 12, stock: 60, unit: "Pcs" },
    { id: 6, code: "PROD006", name: "Philips Air Fryer", category: "Home & Kitchen", price: 8999, tax: 18, stock: 35, unit: "Pcs" },
    { id: 7, code: "PROD007", name: "Sony Headphones WH-1000XM4", category: "Electronics", price: 29999, tax: 18, stock: 22, unit: "Pcs" },
    { id: 8, code: "PROD008", name: "Levi's Denim Jeans", category: "Apparel", price: 3999, tax: 12, stock: 95, unit: "Pcs" },
  ];

  // Sample customers data
  const customersData = [
    { id: 1, code: "CUST001", name: "Rajesh Kumar", phone: "+91 98765 43210", email: "rajesh@email.com", type: "Regular" },
    { id: 2, code: "CUST002", name: "Priya Sharma", phone: "+91 98765 43211", email: "priya@email.com", type: "Premium" },
    { id: 3, code: "CUST003", name: "Walk-in Customer", phone: "", email: "", type: "Walk-in" },
  ];

  const [invoiceNo, setInvoiceNo] = useState(`INV${Date.now()}`);
  const [selectedCustomer, setSelectedCustomer] = useState(customersData[2]); // Walk-in by default
  const [cartItems, setCartItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  
  const [discountType, setDiscountType] = useState("percentage"); // percentage or fixed
  const [discountValue, setDiscountValue] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paidAmount, setPaidAmount] = useState(0);
  const [notes, setNotes] = useState("");
  
  const [successMessage, setSuccessMessage] = useState("");
  const [openPrintDialog, setOpenPrintDialog] = useState(false);
  const [savedInvoice, setSavedInvoice] = useState(null);

  // Calculations
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  const discountAmount = discountType === "percentage" 
    ? (subtotal * discountValue / 100)
    : discountValue;
  
  const afterDiscount = subtotal - discountAmount;
  
  const taxAmount = cartItems.reduce((sum, item) => {
    const itemTotal = item.price * item.quantity;
    const itemDiscount = discountType === "percentage" 
      ? (itemTotal * discountValue / 100)
      : (itemTotal / subtotal) * discountValue;
    const itemAfterDiscount = itemTotal - itemDiscount;
    return sum + (itemAfterDiscount * item.tax / 100);
  }, 0);
  
  const grandTotal = afterDiscount + taxAmount;
  const balance = paidAmount - grandTotal;

  // Handlers
  const handleAddToCart = () => {
    if (!selectedProduct) {
      alert("Please select a product");
      return;
    }
    if (quantity <= 0) {
      alert("Quantity must be greater than 0");
      return;
    }
    if (quantity > selectedProduct.stock) {
      alert(`Only ${selectedProduct.stock} units available in stock`);
      return;
    }

    const existingItem = cartItems.find(item => item.id === selectedProduct.id);
    
    if (existingItem) {
      setCartItems(cartItems.map(item =>
        item.id === selectedProduct.id
          ? { ...item, quantity: item.quantity + quantity }
          : item
      ));
    } else {
      setCartItems([...cartItems, {
        ...selectedProduct,
        quantity: quantity,
      }]);
    }

    setSelectedProduct(null);
    setQuantity(1);
  };

  const handleUpdateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }
    
    const item = cartItems.find(i => i.id === itemId);
    if (newQuantity > item.stock) {
      alert(`Only ${item.stock} units available in stock`);
      return;
    }

    setCartItems(cartItems.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    ));
  };

  const handleRemoveItem = (itemId) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
  };

  const handleClearCart = () => {
    if (window.confirm("Are you sure you want to clear the cart?")) {
      setCartItems([]);
      setDiscountValue(0);
      setPaidAmount(0);
    }
  };

  const handleSaveInvoice = () => {
    if (cartItems.length === 0) {
      alert("Cart is empty. Please add items to create an invoice.");
      return;
    }

    const invoice = {
      invoiceNo,
      date: new Date().toISOString(),
      customer: selectedCustomer,
      items: cartItems,
      subtotal,
      discountType,
      discountValue,
      discountAmount,
      taxAmount,
      grandTotal,
      paymentMethod,
      paidAmount,
      balance,
      notes,
    };

    setSavedInvoice(invoice);
    setSuccessMessage("Invoice saved successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handlePrint = () => {
    handleSaveInvoice();
    setOpenPrintDialog(true);
  };

  const handleNewInvoice = () => {
    setInvoiceNo(`INV${Date.now()}`);
    setSelectedCustomer(customersData[2]);
    setCartItems([]);
    setDiscountValue(0);
    setPaymentMethod("Cash");
    setPaidAmount(0);
    setNotes("");
    setSavedInvoice(null);
    setSuccessMessage("Ready for new invoice");
    setTimeout(() => setSuccessMessage(""), 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReceiptIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              New Billing / Invoice
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create new sales invoice for retail billing • Invoice: {invoiceNo}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleNewInvoice}
            sx={{ borderRadius: 2 }}
          >
            New Invoice
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ borderRadius: 2 }}
            disabled={cartItems.length === 0}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveInvoice}
            disabled={cartItems.length === 0}
            sx={{
              borderRadius: 2,
              px: 3,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Save Invoice
          </Button>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Side - Product Selection & Cart */}
        <Grid item xs={12} md={8}>
          {/* Customer Selection */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon /> Customer Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  value={selectedCustomer}
                  onChange={(event, newValue) => {
                    setSelectedCustomer(newValue);
                  }}
                  options={customersData}
                  getOptionLabel={(option) => `${option.name} (${option.code})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Customer"
                      placeholder="Search customer by name or code"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          {option.name.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="600">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.phone || 'No phone'} • {option.type}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
              {selectedCustomer && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                        <Typography variant="body2" fontWeight="600">{selectedCustomer.name}</Typography>
                      </Grid>
                      {selectedCustomer.phone && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Phone</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCustomer.phone}</Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Customer Type</Typography>
                        <Chip label={selectedCustomer.type} size="small" color="primary" />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Product Selection */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CartIcon /> Add Products
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={selectedProduct}
                  onChange={(event, newValue) => {
                    setSelectedProduct(newValue);
                  }}
                  options={productsData}
                  getOptionLabel={(option) => `${option.name} (${option.code})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Product"
                      placeholder="Search by name or code"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" fontWeight="600">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.code} • {formatCurrency(option.price)} • Stock: {option.stock} • Tax: {option.tax}%
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddToCart}
                  sx={{ 
                    height: 56,
                    backgroundColor: 'success.main',
                    '&:hover': {
                      backgroundColor: 'success.dark',
                    },
                  }}
                >
                  Add to Cart
                </Button>
              </Grid>
            </Grid>

            {selectedProduct && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: '#e3f2fd' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Product</Typography>
                    <Typography variant="body2" fontWeight="600">{selectedProduct.name}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Price</Typography>
                    <Typography variant="body2" fontWeight="600" color="primary">{formatCurrency(selectedProduct.price)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Available Stock</Typography>
                    <Typography variant="body2" fontWeight="600" color="success.main">{selectedProduct.stock} {selectedProduct.unit}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Tax</Typography>
                    <Typography variant="body2" fontWeight="600">{selectedProduct.tax}%</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Paper>

          {/* Cart Items */}
          <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                Cart Items ({cartItems.length})
              </Typography>
              {cartItems.length > 0 && (
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleClearCart}
                  color="error"
                >
                  Clear Cart
                </Button>
              )}
            </Box>
            <Divider />
            {cartItems.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tax %</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cartItems.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.code}</Typography>
                        </TableCell>
                        <TableCell>{formatCurrency(item.price)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                              sx={{ bgcolor: '#f5f5f5' }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" fontWeight="600" sx={{ minWidth: 30, textAlign: 'center' }}>
                              {item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                              sx={{ bgcolor: '#f5f5f5' }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>{item.tax}%</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="700" color="primary">
                            {formatCurrency(item.price * item.quantity)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveItem(item.id)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CartIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">Cart is empty</Typography>
                <Typography variant="body2" color="text.secondary">Add products to start billing</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Side - Summary & Payment */}
        <Grid item xs={12} md={4}>
          {/* Discount */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DiscountIcon /> Discount
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    label="Discount Type"
                  >
                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                    <MenuItem value="fixed">Fixed Amount (₹)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount Value"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {discountType === 'percentage' ? <PercentIcon /> : '₹'}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {discountValue > 0 && (
                <Grid item xs={12}>
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    Discount Applied: {formatCurrency(discountAmount)}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Billing Summary */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalculateIcon /> Billing Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              <ListItem sx={{ px: 0 }}>
                <ListItemText primary="Subtotal" />
                <Typography variant="body1" fontWeight="600">{formatCurrency(subtotal)}</Typography>
              </ListItem>
              {discountValue > 0 && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText primary="Discount" />
                  <Typography variant="body1" fontWeight="600" color="error">
                    - {formatCurrency(discountAmount)}
                  </Typography>
                </ListItem>
              )}
              <ListItem sx={{ px: 0 }}>
                <ListItemText primary="Tax (GST)" />
                <Typography variant="body1" fontWeight="600">{formatCurrency(taxAmount)}</Typography>
              </ListItem>
              <Divider />
              <ListItem sx={{ px: 0, bgcolor: '#f5f5f5', borderRadius: 1, mt: 1 }}>
                <ListItemText 
                  primary={<Typography variant="h6" fontWeight="bold">Grand Total</Typography>} 
                />
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {formatCurrency(grandTotal)}
                </Typography>
              </ListItem>
            </List>
          </Paper>

          {/* Payment */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon /> Payment Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    label="Payment Method"
                  >
                    <MenuItem value="Cash">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon /> Cash
                      </Box>
                    </MenuItem>
                    <MenuItem value="Card">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CardIcon /> Card
                      </Box>
                    </MenuItem>
                    <MenuItem value="UPI">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QRIcon /> UPI
                      </Box>
                    </MenuItem>
                    <MenuItem value="Net Banking">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BankIcon /> Net Banking
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount Paid"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: balance >= 0 ? '#e8f5e9' : '#ffebee' }}>
                  <Typography variant="caption" color="text.secondary">
                    {balance >= 0 ? 'Change to Return' : 'Balance Due'}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color={balance >= 0 ? 'success.main' : 'error.main'}>
                    {formatCurrency(Math.abs(balance))}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes (Optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes for this invoice"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Quick Actions */}
          <Card sx={{ borderRadius: 2, boxShadow: 2, backgroundColor: 'primary.main' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="white" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setPaidAmount(grandTotal)}
                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    Exact Amount
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setPaidAmount(Math.ceil(grandTotal / 100) * 100)}
                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    Round Off
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Print Dialog */}
      <Dialog open={openPrintDialog} onClose={() => setOpenPrintDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrintIcon /> Invoice Preview
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {savedInvoice && (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid #000' }}>
                <Typography variant="h5" fontWeight="bold">YOUR STORE NAME</Typography>
                <Typography variant="body2">123 Store Address, City - 400001</Typography>
                <Typography variant="body2">Phone: +91 12345 67890 | Email: store@email.com</Typography>
                <Typography variant="body2">GSTIN: 27AAAAA0000A1Z5</Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Invoice No:</Typography>
                  <Typography variant="body2" fontWeight="600">{savedInvoice.invoiceNo}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Date:</Typography>
                  <Typography variant="body2" fontWeight="600">
                    {new Date(savedInvoice.date).toLocaleDateString('en-IN')}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Customer:</Typography>
                <Typography variant="body1" fontWeight="bold">{savedInvoice.customer.name}</Typography>
                {savedInvoice.customer.phone && (
                  <Typography variant="body2">{savedInvoice.customer.phone}</Typography>
                )}
              </Box>

              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savedInvoice.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.price)}</TableCell>
                        <TableCell>{formatCurrency(item.price * item.quantity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ textAlign: 'right', mb: 2 }}>
                <Typography variant="body2">Subtotal: {formatCurrency(savedInvoice.subtotal)}</Typography>
                {savedInvoice.discountAmount > 0 && (
                  <Typography variant="body2">Discount: - {formatCurrency(savedInvoice.discountAmount)}</Typography>
                )}
                <Typography variant="body2">Tax (GST): {formatCurrency(savedInvoice.taxAmount)}</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                  Grand Total: {formatCurrency(savedInvoice.grandTotal)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Payment: {savedInvoice.paymentMethod} - {formatCurrency(savedInvoice.paidAmount)}
                </Typography>
                {savedInvoice.balance >= 0 && (
                  <Typography variant="body2">Change: {formatCurrency(savedInvoice.balance)}</Typography>
                )}
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3, pt: 2, borderTop: '1px dashed #000' }}>
                <Typography variant="caption">Thank you for your business!</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenPrintDialog(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            Print Invoice
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewBilling;

